import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';

import { APP_PATH, appUrl } from '../constants/app-paths';
import { MembershipSessionService } from '../services/membership-session.service';

/**
 * Keeps /my-membership behind a live OTP session. A functional guard (review MR !1 N21).
 *
 * It is a REDIRECT to the verify screen, never a refusal page: a member whose
 * 30-minute token has expired has done nothing wrong, and "verify your number
 * again" is the whole remedy. Redirecting also means a bookmarked or shared link
 * lands somewhere useful instead of on an error.
 */
export const membershipSessionGuard: CanActivateFn = (_route, state) => {
  if (inject(MembershipSessionService).hasSession) {
    return true;
  }
  return inject(Router).createUrlTree([appUrl(APP_PATH.VERIFY)], { queryParams: { next: nextFromUrl(state.url) } });
};

/** The sign-in screen's own path. It is never a destination — see `safeNextPath`. */
const SIGN_IN_PATH: string = APP_PATH.VERIFY;
const DEFAULT_NEXT: string = APP_PATH.MY_MEMBERSHIP;

/**
 * Where to return after verifying: the guarded path the member asked for (`refer`,
 * `my-membership/renew`), without its query string. Anything unexpected falls back to My membership.
 */
export function nextFromUrl(url: string | undefined): string {
  return safeNextPath((url ?? '').split(/[?#]/)[0]);
}

/**
 * ⚠ THE SIGN-IN SCREEN IS NOT SOMEWHERE TO COME BACK TO, and excluding it is what stops a
 * successful sign-in appearing to do nothing (owner, 2026-09-21: *"after sign in the screen do
 * not change it shows the OTP and stays there"*).
 *
 * The account menu's Sign in reuses `nextFromUrl` so a member is returned to whatever they were
 * doing — right everywhere except ON this screen, where "where they were" IS the sign-in screen.
 * `next` then came back as `verify`, the code verified, and the router navigated to the route
 * already on screen. THE SESSION REALLY DID START — the header switched to the member's name — so
 * nothing threw, nothing logged, and the only visible symptom was a page that did not move.
 * Pressing Sign in again then spends the consumed code and answers "That code is not valid",
 * which reads as a broken sign-in rather than as a second attempt on a code already used.
 *
 * Both the producers (the guard, the account menu) and the consumer (the verify screen) run
 * through here, so neither side can reintroduce it alone.
 */
export function safeNextPath(candidate: string | null | undefined): string {
  const path = (candidate ?? '').replace(/^\/+/, '');
  if (!/^[a-z0-9-]+(\/[a-z0-9-]+)*$/i.test(path)) {
    return DEFAULT_NEXT;
  }
  const lower = path.toLowerCase();
  return lower === SIGN_IN_PATH || lower.startsWith(`${SIGN_IN_PATH}/`) ? DEFAULT_NEXT : path;
}
